import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToSection } from "./Navbar";
import { useContent } from "@/content/ContentContext";
import AutoVideo from "./AutoVideo";

const EASE = [0.16, 1, 0.3, 1];

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.0, ease: EASE, delay: 0.35 + i * 0.16 },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: EASE, delay: d },
  }),
};

export const isVideoUrl = (url) => /\.(mp4|webm|mov|m4v|ogg)(\?.*)?$/i.test(url || "");

export default function HeroSection() {
  const { content } = useContent();
  const hero = content.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section id="hero" ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      <motion.div
        aria-hidden="true"
        style={{ opacity: glowOpacity }}
        className="absolute -top-40 right-[-10%] w-[55vw] h-[55vw] gold-glow-radial pointer-events-none"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center min-h-[100vh] pt-32 pb-20">
        {/* Left: kinetic narrative */}
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.15}
            className="font-mono-lux text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold mb-8"
            data-testid="hero-eyebrow"
          >
            {hero.eyebrow}
          </motion.p>

          <h1 className="font-serif-lux tracking-tight leading-[1.06] text-[#F7F7F8]" data-testid="hero-headline">
            <span className="block overflow-hidden pb-1">
              <motion.span
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                custom={0}
                className="block text-4xl sm:text-5xl lg:text-[4.2rem] italic text-gold-champagne"
              >
                {hero.line1}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                custom={1}
                className="block text-4xl sm:text-5xl lg:text-[4.2rem]"
              >
                {hero.line2}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                custom={2}
                className="block text-4xl sm:text-5xl lg:text-[4.2rem]"
              >
                {hero.line3Pre} <span className="italic text-gold">{hero.line3Accent}</span> {hero.line3Post}
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1.0}
            className="mt-8 max-w-xl text-base sm:text-lg font-sans-lux text-[#B8B8C2] leading-relaxed"
            data-testid="hero-subtext"
          >
            {hero.subtext}
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1.25}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#narrative"
              onClick={(e) => { e.preventDefault(); scrollToSection("#narrative"); }}
              data-testid="hero-primary-cta"
              className="btn-shimmer inline-flex items-center gap-2 bg-gold text-[#0A0A0B] font-semibold text-sm tracking-wide px-7 py-3.5 hover:bg-gold-champagne transition-colors duration-300 active:scale-[0.98]"
            >
              {hero.ctaPrimary}
              <ArrowDown size={15} />
            </a>
            <a
              href="#ecentra-run"
              onClick={(e) => { e.preventDefault(); scrollToSection("#ecentra-run"); }}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 border border-[rgba(212,175,55,0.45)] text-gold-champagne text-sm tracking-wide px-7 py-3.5 hover:border-[rgba(243,227,166,0.7)] hover:bg-[rgba(212,175,55,0.07)] transition-colors duration-300 active:scale-[0.98]"
            >
              {hero.ctaSecondary}
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1.5}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 max-w-2xl divide-x divide-[rgba(212,175,55,0.18)] border-y border-[rgba(212,175,55,0.18)]"
            data-testid="hero-stats"
          >
            {hero.stats.map((s, i) => (
              <div key={i} className="px-4 sm:px-6 py-5 first:pl-0">
                <p className="font-serif-lux text-2xl sm:text-3xl text-gold">{s.n}</p>
                <p className="mt-1 text-[11px] sm:text-xs text-[#757582] leading-snug">{s.l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: portrait with parallax */}
        <div className="lg:col-span-5 relative z-10">
          <motion.div
            style={{ y: portraitY }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, ease: EASE, delay: 0.5 }}
            className="relative max-w-md mx-auto"
            data-testid="hero-portrait-frame"
          >
            <div aria-hidden="true" className="absolute -inset-8 gold-glow-radial" />
            {/* gold corner anchors */}
            <span aria-hidden="true" className="absolute -top-3 -left-3 w-10 h-10 border-t-2 border-l-2 border-gold" />
            <span aria-hidden="true" className="absolute -top-3 -right-3 w-10 h-10 border-t-2 border-r-2 border-gold" />
            <span aria-hidden="true" className="absolute -bottom-3 -left-3 w-10 h-10 border-b-2 border-l-2 border-gold" />
            <span aria-hidden="true" className="absolute -bottom-3 -right-3 w-10 h-10 border-b-2 border-r-2 border-gold" />
            <div className="relative overflow-hidden border border-[rgba(212,175,55,0.35)] shadow-[0_0_80px_rgba(212,175,55,0.12)]">
              {isVideoUrl(hero.portrait) ? (
                <AutoVideo
                  src={hero.portrait}
                  className="w-full h-auto block grayscale contrast-105"
                  testId="hero-portrait-image"
                />
              ) : (
                <img
                  src={hero.portrait}
                  alt="Sam Ortega — Entrepreneur, Leader, Builder, Mentor"
                  className="w-full h-auto block grayscale contrast-105"
                  data-testid="hero-portrait-image"
                />
              )}
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[rgba(7,7,8,0.55)] via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -inset-x-3 flex justify-center pointer-events-none">
              <motion.div
                animate={{ y: [0, -7, 0] }}
                transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut" }}
                className="bg-[rgba(15,15,18,0.92)] backdrop-blur-md border border-[rgba(212,175,55,0.4)] px-5 py-2.5 shadow-[0_8px_40px_rgba(0,0,0,0.55)]"
                data-testid="hero-floating-badge"
              >
                <span className="block text-center font-mono-lux text-[10px] tracking-[0.22em] leading-relaxed text-gold">
                  {hero.badge}
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
